/**
 * Knowledge Base Action Atoms - Write operations for Knowledge Bases
 *
 * These atoms handle all KB mutations (create, update, delete).
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { atom } from "jotai";
import {
  KnowledgeBaseSchema,
  KnowledgeBaseListItemSchema,
  type CreateKnowledgeBaseBody,
  type UpdateKnowledgeBaseBody,
} from "@/isomorphic/schemas";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidated, fetchValidatedArray, postValidated, putValidated } from "@/frontend/lib/validation";
import {
  _apiWorkspaceKnowledgeBasesAtom,
  workspaceKnowledgeBasesStatusAtom,
  workspaceKnowledgeBasesErrorAtom,
  currentKnowledgeBaseAtom,
  currentKnowledgeBaseStatusAtom,
} from "@/frontend/lib/atoms/core/knowledge-bases";

// ============================================================================
// FETCH ACTIONS
// ============================================================================

/**
 * Fetch all knowledge bases for a workspace
 */
export const fetchWorkspaceKnowledgeBasesAtom = atom(
  null,
  async (get, set, args: { jwt: string; workspaceId: string }) => {
    const { jwt, workspaceId } = args;
    if (!jwt || !workspaceId) {
      return;
    }

    set(workspaceKnowledgeBasesStatusAtom, "loading");
    set(workspaceKnowledgeBasesErrorAtom, null);

    const result = await fetchValidatedArray(
      KnowledgeBaseListItemSchema,
      API_ROUTES.knowledgeBases.getByWorkspace(workspaceId),
      { jwt, context: "Workspace Knowledge Bases" },
    );

    if (result.success) {
      set(_apiWorkspaceKnowledgeBasesAtom, result.data);
      set(workspaceKnowledgeBasesStatusAtom, "success");
      return result.data;
    } else {
      console.error("[KnowledgeBaseActions] Error fetching knowledge bases:", result.error);
      set(workspaceKnowledgeBasesStatusAtom, "error");
      set(workspaceKnowledgeBasesErrorAtom, result.error);
      throw new Error(result.error);
    }
  },
);

/**
 * Fetch a single knowledge base by ID
 */
export const fetchKnowledgeBaseAtom = atom(null, async (get, set, args: { jwt: string; kbId: string }) => {
  const { jwt, kbId } = args;
  if (!jwt || !kbId) {
    return null;
  }

  set(currentKnowledgeBaseStatusAtom, "loading");

  const result = await fetchValidated(KnowledgeBaseSchema, API_ROUTES.knowledgeBases.getById(kbId), {
    jwt,
    context: `Knowledge Base ${kbId}`,
  });

  if (result.success) {
    set(currentKnowledgeBaseAtom, result.data);
    set(currentKnowledgeBaseStatusAtom, "success");
    return result.data;
  } else {
    console.error("[KnowledgeBaseActions] Error fetching knowledge base:", result.error);
    set(currentKnowledgeBaseStatusAtom, "error");
    return null;
  }
});

// ============================================================================
// MUTATION ACTIONS
// ============================================================================

/**
 * Create a new knowledge base
 */
export const createKnowledgeBaseAtom = atom(
  null,
  async (get, set, args: { jwt: string; payload: CreateKnowledgeBaseBody }) => {
    const { jwt, payload } = args;

    if (!jwt) {
      return null;
    }

    const result = await postValidated(KnowledgeBaseSchema, API_ROUTES.knowledgeBases.create, payload, {
      jwt,
      context: "Create Knowledge Base",
    });

    if (result.success) {
      // Add the new KB to the list
      const currentKBs = get(_apiWorkspaceKnowledgeBasesAtom);
      set(_apiWorkspaceKnowledgeBasesAtom, [...currentKBs, result.data]);
      return result.data;
    } else {
      console.error("[KnowledgeBaseActions] Error creating knowledge base:", result.error);
      throw new Error(result.error);
    }
  },
);

/**
 * Update an existing knowledge base
 */
export const updateKnowledgeBaseAtom = atom(
  null,
  async (get, set, args: { jwt: string; kbId: string; payload: Partial<UpdateKnowledgeBaseBody> }) => {
    const { jwt, kbId, payload } = args;

    if (!jwt || !kbId) {
      return null;
    }

    const result = await putValidated(KnowledgeBaseSchema, API_ROUTES.knowledgeBases.update(kbId), payload, {
      jwt,
      context: `Update Knowledge Base ${kbId}`,
    });

    if (result.success) {
      // Update the KB in the list with computed counts
      const currentKBs = get(_apiWorkspaceKnowledgeBasesAtom);
      const updatedKB = result.data;
      set(
        _apiWorkspaceKnowledgeBasesAtom,
        currentKBs.map((kb) =>
          kb.id === kbId
            ? {
                ...kb,
                ...updatedKB,
                // Compute counts from arrays for list display
                filesCount: updatedKB.files?.length ?? 0,
                faqsCount: updatedKB.faqs?.length ?? 0,
                infoBlocksCount: updatedKB.infoBlocks?.length ?? 0,
              }
            : kb,
        ),
      );
      return result.data;
    } else {
      console.error("[KnowledgeBaseActions] Error updating knowledge base:", result.error);
      throw new Error(result.error);
    }
  },
);

// ============================================================================
// FILE UPLOAD FUNCTION
// ============================================================================

/**
 * Upload a file to a knowledge base
 * Uses FormData for file upload
 */
export const uploadKnowledgeBaseFile = async (
  jwt: string,
  kbId: string,
  file: File,
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!jwt || !kbId) {
      return { success: false, error: "Missing parameters" };
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(API_ROUTES.knowledgeBases.uploadFile(kbId), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.error || "Failed to upload file" };
    }

    const result = await response.json();
    return { success: true, ...result };
  } catch (error) {
    console.error("[KnowledgeBaseActions] Error uploading file:", error);
    return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
  }
};

/**
 * Delete a knowledge base
 * Note: Cannot delete a KB that is linked to an agent - must archive instead
 */
export const deleteKnowledgeBaseAtom = atom(
  null,
  async (
    get,
    set,
    args: { jwt: string; kbId: string },
  ): Promise<{ success: boolean; error?: string; mustArchive?: boolean }> => {
    const { jwt, kbId } = args;

    if (!jwt || !kbId) {
      return { success: false, error: "Missing required parameters" };
    }

    try {
      const response = await fetch(API_ROUTES.knowledgeBases.delete(kbId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data.error || data.message || `HTTP error ${response.status}`;

        // Check if KB is linked to an agent
        if (errorMessage.includes("linked to an agent") || errorMessage.includes("Archive it instead")) {
          return { success: false, error: errorMessage, mustArchive: true };
        }

        return { success: false, error: errorMessage };
      }

      // Remove KB from the list
      const currentKBs = get(_apiWorkspaceKnowledgeBasesAtom);
      set(
        _apiWorkspaceKnowledgeBasesAtom,
        currentKBs.filter((kb) => kb.id !== kbId),
      );

      return { success: true };
    } catch (error) {
      console.error("[KnowledgeBaseActions] Error deleting knowledge base:", error);
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  },
);

/**
 * Archive a knowledge base (soft delete)
 * Use this when the KB is linked to an agent and cannot be deleted
 */
export const archiveKnowledgeBaseAtom = atom(
  null,
  async (get, set, args: { jwt: string; kbId: string }): Promise<{ success: boolean; error?: string }> => {
    const { jwt, kbId } = args;

    if (!jwt || !kbId) {
      return { success: false, error: "Missing required parameters" };
    }

    try {
      const response = await fetch(API_ROUTES.knowledgeBases.archive(kbId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        return { success: false, error: data.error || data.message || `HTTP error ${response.status}` };
      }

      // Remove KB from the list (archived KBs are not shown in the main list)
      const currentKBs = get(_apiWorkspaceKnowledgeBasesAtom);
      set(
        _apiWorkspaceKnowledgeBasesAtom,
        currentKBs.filter((kb) => kb.id !== kbId),
      );

      return { success: true };
    } catch (error) {
      console.error("[KnowledgeBaseActions] Error archiving knowledge base:", error);
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  },
);
